import { postMessageService } from '@/modules/common/services';
import { poll } from '@/modules/common/utils';

const state = {
  replyContent: {
    email_message: '',
    sms_message: '',
    FB_message: '',
    other_message: '',
  },
  isLoaded: false,
  isLoading: false,
  currentMatterUid: null,
};

const getters = {
  replyContent: (state) => state.replyContent,
  isLoaded: (state) => state.isLoaded,
  isLoading: (state) => state.isLoading,
  currentMatterUid: (state) => state.currentMatterUid,
  isMatterMismatch: (state) => (matterUid) => {
    // Check if cached data is for a different matter
    // But NOT if we're currently loading that matter (prevents infinite loop)
    const isWrongMatter = state.isLoaded && matterUid && state.currentMatterUid !== matterUid;
    const isAlreadyLoadingCorrectMatter = state.isLoading && state.currentMatterUid === matterUid;
    return isWrongMatter && !isAlreadyLoadingCorrectMatter;
  },
  getReplyByChannel: (state) => (channelName, matterUid = null) => {
    // Validate that cached reply is for the correct matter before returning
    if (!state.isLoaded) return '';
    if (matterUid && state.currentMatterUid !== matterUid) return '';
    
    switch (channelName) {
      case 'email': return state.replyContent.email_message;
      case 'sms': return state.replyContent.sms_message;
      case 'messenger':
      case 'facebookmessenger':
      case 'facebookmessenger_447':
        return state.replyContent.FB_message;
      default: return state.replyContent.other_message;
    }
  },
};

const mutations = {
  setReplyContent(state, content) {
    state.replyContent = {
      email_message: content.email_message || '',
      sms_message: content.sms_message || '',
      FB_message: content.FB_message || '',
      other_message: content.other_message || '',
    };
    
    // CRITICAL FIX: Only set isLoaded = true if we actually have content!
    // Otherwise polling stops too early when backend hasn't generated reply yet
    const hasContent = !!(
      content.email_message ||
      content.sms_message ||
      content.FB_message ||
      content.other_message
    );
    
    state.isLoaded = hasContent;
    state.isLoading = false;
  },
  clearReplyContent(state) {
    state.replyContent = {
      email_message: '',
      sms_message: '',
      FB_message: '',
      other_message: '',
    };
    state.isLoaded = false;
    state.isLoading = false;
    state.currentMatterUid = null;
  },
  setLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setLoaded(state, isLoaded) {
    state.isLoaded = isLoaded;
  },
  setCurrentMatterUid(state, matterUid) {
    state.currentMatterUid = matterUid;
  },
};

const actions = {
  async loadSmartReply({ commit, state, dispatch }, matterUid) {
    // Validate matterUid - must be a non-empty string/number
    if (!matterUid || (typeof matterUid === 'string' && !matterUid.trim())) {
      console.warn('[Vue SmartReplyStore] loadSmartReply called with invalid matterUid:', matterUid);
      commit('clearReplyContent');
      return;
    }

    // Check cache: Already loaded for this matter?
    if (state.currentMatterUid === matterUid && state.isLoaded) {
      return; // Use cached data
    }

    // Check if already loading this matter
    if (state.currentMatterUid === matterUid && state.isLoading) {
      return; // Already in progress, don't start another loop
    }

    // Clear previous data if switching matters
    if (state.currentMatterUid !== matterUid) {
      commit('clearReplyContent');
      // CRITICAL: Also clear POV store to prevent stale data from being fetched
      postMessageService.post('povMessage', {
        eventName: 'update-store',
        payload: {
          type: 'SmartReplyStore/clearSmartReply',
          payload: null,
        },
      });
    }

    commit('setCurrentMatterUid', matterUid);
    commit('setLoading', true);

    // Generate unique requestId to track this specific request
    const requestId = `load-${Date.now()}-${Math.floor(Math.random() * 999999)}`;

    // Step 1: Tell POV to load smart replies
    postMessageService.post('povMessage', {
      eventName: 'update-store',
      payload: {
        type: 'SmartReplyStore/loadSmartReply',
        payload: { matterUid, requestId },
      },
    });

    // Step 2: Start retry loop (handles all components)
    dispatch('fetchSmartReplyWithRetry', { matterUid, requestId });
  },

  async fetchSmartReplyWithRetry({ commit, state }, { matterUid, requestId }) {
    if (state.currentMatterUid !== matterUid) {
      return;
    }
    
    const replyContent = await poll(
      async () => {
        if (state.currentMatterUid !== matterUid) {
          return null;
        }

        try {
          const povRequestId = await postMessageService.getDataFromPOVStore('SmartReplyStore/currentRequestId');
          if (povRequestId.data !== requestId) {
            return null;
          }

          const povLoading = await postMessageService.getDataFromPOVStore('SmartReplyStore/isLoading');
          if (povLoading.data === false) {
            const verifyRequestId = await postMessageService.getDataFromPOVStore('SmartReplyStore/currentRequestId');
            if (verifyRequestId.data === requestId) {
              const data = await postMessageService.getDataFromPOVStore('SmartReplyStore/replyContent');
              return data.data;
            }
          }
          return null;
        } catch (error) {
          console.warn('[Vue SmartReplyModule] Error fetching from POV:', error);
          return null;
        }
      },
      (replyContent) => {
        if (state.currentMatterUid !== matterUid) {
          return true;
        }
        const hasContent = replyContent && (
          replyContent.email_message ||
          replyContent.sms_message ||
          replyContent.FB_message ||
          replyContent.other_message
        );
        return hasContent;
      },
      1000,
      20000 // 20s timeout (POV times out at 15s + 5s buffer)
    );

    if (replyContent && state.currentMatterUid === matterUid) {
      commit('setReplyContent', replyContent);
      commit('setLoaded', true);
    } else if (state.currentMatterUid === matterUid) {
      commit('clearReplyContent');
    }
    
    if (state.currentMatterUid === matterUid) {
      commit('setLoading', false);
    }
  },

  async generateSmartReply({ commit, state }, { 
    matterUid, 
    clientUid, 
    populatedText, 
    customInstructions,
    userInputReferenceId = 'prmpt-smart-reply-00001'
  }) {
    if (!matterUid || (typeof matterUid === 'string' && !matterUid.trim())) {
      console.warn('[Vue SmartReplyStore] generateSmartReply called with invalid matterUid:', matterUid);
      return null;
    }
    if (!clientUid || (typeof clientUid === 'string' && !clientUid.trim())) {
      console.warn('[Vue SmartReplyStore] generateSmartReply called with invalid clientUid:', clientUid);
      return null;
    }
    
    commit('setCurrentMatterUid', matterUid);
    commit('setLoading', true);

    const requestId = `gen-${Date.now()}-${Math.floor(Math.random() * 999999)}`;

    postMessageService.post('povMessage', {
      eventName: 'update-store',
      payload: {
        type: 'SmartReplyStore/generateSmartReply',
        payload: {
          matterUid,
          clientUid,
          userInputReferenceId,
          populatedText,
          customInstructions,
          requestId,
        },
      },
    });

    const replyContent = await poll(
      async () => {
        if (state.currentMatterUid !== matterUid) {
          return { cancelled: true };
        }

        try {
          const povRequestId = await postMessageService.getDataFromPOVStore('SmartReplyStore/currentRequestId');
          if (povRequestId.data !== requestId) {
            return null;
          }

          const povLoading = await postMessageService.getDataFromPOVStore('SmartReplyStore/isLoading');
          if (povLoading.data === false) {
            const verifyRequestId = await postMessageService.getDataFromPOVStore('SmartReplyStore/currentRequestId');
            if (verifyRequestId.data === requestId) {
              const data = await postMessageService.getDataFromPOVStore('SmartReplyStore/replyContent');
              return data.data;
            }
          }
        } catch (error) {
          console.error('[Vue SmartReplyModule] Error polling POV:', error);
        }
        return null;
      },
      async (result) => {
        if (result?.cancelled) {
          return true;
        }
        return result && (
          result.email_message ||
          result.sms_message ||
          result.FB_message ||
          result.other_message
        );
      },
      1000,
      30000 // 30s timeout for generation
    );

    if (replyContent?.cancelled) {
      commit('setLoading', false);
      return null;
    }

    if (replyContent) {
      commit('setReplyContent', replyContent);
      commit('setLoading', false);
      return replyContent;
    }

    console.warn('[Vue SmartReplyModule] Smart reply generation timeout or no content after 30 seconds');
    commit('setLoading', false);
    return null;
  },

  clearReplyContent({ commit }) {
    commit('clearReplyContent');
    
    // Also clear POV store
    postMessageService.post('povMessage', {
      eventName: 'update-store',
      payload: {
        type: 'SmartReplyStore/clearSmartReply',
        payload: null,
      },
    });
  },

  // Save generated reply to store (from dropdown generation)
  saveGeneratedReply({ commit, state }, { matterUid, replyContent }) {
    // Validate matterUid
    if (!matterUid || (typeof matterUid === 'string' && !matterUid.trim())) {
      console.warn('[Vue SmartReplyStore] saveGeneratedReply called with invalid matterUid:', matterUid);
      return;
    }

    // Update Vue store
    commit('setCurrentMatterUid', matterUid);
    commit('setReplyContent', replyContent);
    
    // Also update POV store
    postMessageService.post('povMessage', {
      eventName: 'update-store',
      payload: {
        type: 'SmartReplyStore/setReplyContent',
        payload: replyContent,
      },
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

